import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { isLoadBetSupportedWithShare } from '@core/Areas/ShareBet/ShareBetOutgoing/helpers'
import type { ShareBetData } from '@core/Areas/ShareBet/types'
import { state$ as userAccountState } from '@core/State/UserAccount/userAccountDriver'
import { useObservableImmutable } from '@core/Utils/hooks'
import { useGetCountryEntitlements } from '@core/Utils/hooks/useGetCountryEntitlements'
import { BetLoopShare } from './Components/BetLoopShare'
import { BetSlipShare } from './Components/BetSlipShare'
import { PreviewDisplayFrame } from './Components/Common/PreviewDisplayFrame'
import { KambiSportsCouponShare } from './Components/KambiSportsShare'
import { QuickbetShare } from './Components/QuickbetShare'
import { TicketShare } from './Components/TicketShare'
import { handleShareBetClick } from './helpers/handleShareBetClick'
import { handleShareBetLoopClick } from './helpers/handleShareBetLoopClick'

export const ShareWithExternal: React.FC<{ data: ShareBetData; dismissTray(): void }> = ({
  data,
  dismissTray,
}) => {
  const previewImageElRef = React.useRef<HTMLDivElement>(null)
  const [isCostShared, setShouldHideCost] = React.useState(false)

  const isCountryEntitled = useGetCountryEntitlements()
  const { isShareMyBetBlocked } = useObservableImmutable(userAccountState, ['isShareMyBetBlocked'])
  const isEntitled = isCountryEntitled && !isShareMyBetBlocked

  const isBetLoopShare = data.contextType === 'betloop'
  const shouldAllowLink = shouldAllowLinkSharing(data) && isLoadBetSupportedWithShare(data)

  const { mutate, isError, isPending } = useMutation({
    mutationFn: () => {
      const previewEl = previewImageElRef.current
      return isBetLoopShare
        ? handleShareBetLoopClick({ data, previewEl })
        : handleShareBetClick({ data, previewEl, shouldAllowLink, isCostShared })
    },
  })

  const shouldEnableCostToggle = determineShouldEnableCostToggle(data)
  const handleToggleShouldShowCost = () => setShouldHideCost(curr => !curr)

  React.useEffect(() => {
    if (!isEntitled) {
      addToast({
        id: 'external-share-blocked',
        isDismissable: true,
        title: !isEntitled
          ? 'You don’t currently have access to Share My Bet'
          : 'Your bet cannot be shared',
        icon: 'warning',
        theme: 'dark',
      })
      dismissTray()
    }
  }, [dismissTray, isEntitled])

  if (!isEntitled) {
    return null
  }

  return (
    <PreviewDisplayFrame
      isError={isError}
      isLoading={isPending}
      handleShareClick={mutate}
      shouldShowCost={isCostShared}
      shareImageElRef={previewImageElRef}
      key={`force-rerender-${isCostShared}`}
      handleToggleShouldShowCost={shouldEnableCostToggle ? handleToggleShouldShowCost : undefined}
    >
      <>
        {data.contextType === 'ticket' && (
          <TicketShare share={data.share} isCostShared={isCostShared} />
        )}

        {data.contextType === 'betslip' && (
          <BetSlipShare share={data.share} isCostShared={isCostShared} />
        )}

        {data.contextType === 'quickbet' && (
          <QuickbetShare share={data.share} isCostShared={isCostShared} />
        )}

        {data.contextType === 'kambi' && (
          <KambiSportsCouponShare share={data.share} isCostShared={isCostShared} />
        )}

        {isBetLoopShare && <BetLoopShare share={data.share} />}
      </>
    </PreviewDisplayFrame>
  )
}

// =============
// Local Helpers
// =============

function shouldAllowLinkSharing(data: ShareBetData): boolean {
  if (data.contextType === 'betslip') return true
  if (data.contextType === 'quickbet') return true
  if (data.contextType === 'kambi') return true

  return data.contextType === 'ticket' && data.share.activeTab === 'pending'
}

function determineShouldEnableCostToggle(data: ShareBetData): boolean {
  if (data.contextType === 'betloop') return false
  if (data.contextType === 'kambi') return true
  if (data.contextType === 'betslip') return data.share.isBetPlaced

  if (data.contextType === 'quickbet') {
    const { win, place } = data.share.items[0].investment
    const hasValidInvestment = !!(win.value || place.value)
    return hasValidInvestment
  }

  return true
}
